<script lang="ts">
  import { storyblokEditable } from "@storyblok/svelte";
  import { getContext } from "svelte";
  import { deviceDetails } from "$lib/consumer/stores/deviceDetails";
  import { modalContent, modalIsOpened } from "$stores";
  import { openLink } from "$lib/consumer/utils/helpers";

  // Import types
  import type {
    ModalStoryblok,
    ModalVideoStoryblok,
    SocialLinkStoryblok,
    VideoYoutubeStoryblok,
  } from "$types/components";

  // Get the block data
  export let blok;

  // Get the device details
  $: ({ deviceType, isMobile } = $deviceDetails);

  // Get the social media links
  const socials = getContext("socials") as SocialLinkStoryblok[];

  /**
   * Opens a YouTube video in a modal
   * @param mobileVideo
   * @param desktopVideo
   */
  function openYoutubeModal(videoUrl: string) {
    $modalContent = {
      component: "modal",
      content: [
        {
          component: "videoYoutube",
          embedLink: videoUrl,
          autoplay: true,
          title: "",
        } as VideoYoutubeStoryblok,
      ],
      showCloseButton: true,
      allowKeyboardInput: true,
      fadeIn: true,
      containInScreen: true,
    } as ModalStoryblok;

    $modalIsOpened = true;
  }

  /**
   * Opens a Storyblok video in a modal
   * @param video
   */
  function openVideoModal(mobileVideoUrl: string, desktopVideoUrl: string) {
    $modalContent = {
      component: "modal",
      content: [
        {
          component: "video",
          media: {
            filename: isMobile ? mobileVideoUrl : desktopVideoUrl,
          },
          autoplay: true,
          useModal: false,
          usePlaceholder: false,
        } as ModalVideoStoryblok,
      ],
      showCloseButton: true,
      allowKeyboardInput: true,
      fadeIn: true,
      containInScreen: true,
    } as ModalStoryblok;

    $modalIsOpened = true;
  }
</script>

<section class="bento-box-section section-header-width mx-auto" use:storyblokEditable={blok}>
  <h1 class="main-heading type:h2">{blok.heading}</h1>

  <div class="bento-box">
    {#each blok.bentoCards as bentoCard}
      <!-- Quote Card -->
      {#if bentoCard.component === "bentoCardQuote"}
        <div
          class="bento-card {bentoCard.component}"
          style="background-color: {bentoCard.cardColor.color}"
        >
          <div class="bento-card-content">
            <span class="bento-card-quotation">{bentoCard.quotation}</span>
            <div class="bento-card-author-location-wrapper">
              <span class="bento-card-author">{bentoCard.author}</span>
              <span class="bento-card-location">{bentoCard.location}</span>
            </div>
          </div>
        </div>
      {/if}

      <!-- Link Card -->
      {#if bentoCard.component === "bentoCardLink"}
        <div
          class="bento-card {bentoCard.component} scale-up-on-hover"
          style="background-image: url('{bentoCard.image?.filename}');"
          on:click={() => openLink(bentoCard.link)}
          on:keydown={(e) => e.key === "Enter" && openLink(bentoCard.link)}
          role="button"
          tabindex="0"
        >
          <div class="bento-card-content">
            <div class="bento-card-icon-text-wrapper">
              <img
                class="bento-card-icon"
                src="/images/home/bento/bento-icon-link.svg"
                alt="Link icon"
              />
              <p class="bento-card-text">
                <span class="bento-card-caption">{bentoCard.caption}</span>
              </p>
            </div>
          </div>
        </div>
      {/if}

      <!-- Image Card -->
      {#if bentoCard.component === "bentoCardImage"}
        <div
          class="bento-card {bentoCard.component}"
          style="background-image: url('{bentoCard.image?.filename}');"
        >
          <div class="bento-card-content">
            <div class="bento-card-icon-text-wrapper">
              <img
                class="bento-card-icon"
                src="/images/home/bento/bento-icon-{bentoCard.type}.svg"
                alt="{bentoCard.type} icon"
              />
              <p class="bento-card-text">
                <span class="bento-card-caption">{bentoCard.caption}</span>
              </p>
            </div>
          </div>
        </div>
      {/if}

      <!-- Storyblok Video Card -->
      {#if bentoCard.component === "bentoCardVideo" && bentoCard.video[0]?.component === "video"}
        <div
          class="bento-card {bentoCard.component} scale-up-on-hover"
          style="background-image: url('{bentoCard.image?.filename}');"
          on:click={() =>
            openVideoModal(
              bentoCard.video[0]?.media.filename,
              bentoCard.video[0]?.mediaDesktop.filename,
            )}
          on:keydown={(e) =>
            e.key === "Enter" &&
            openVideoModal(
              bentoCard.video[0]?.media.filename,
              bentoCard.video[0]?.mediaDesktop.filename,
            )}
          role="button"
          tabindex="0"
        >
          <div class="bento-card-content">
            <div class="bento-card-icon-text-wrapper">
              <img
                class="bento-card-icon"
                src="/images/home/bento/bento-icon-video.svg"
                alt="Video icon"
              />
              <p class="bento-card-text">
                <span class="bento-card-caption">{bentoCard.caption}</span>
              </p>
            </div>
          </div>
        </div>
      {/if}

      <!-- YouTube Video Card -->
      {#if bentoCard.component === "bentoCardVideo" && bentoCard.video[0]?.component === "videoYoutube"}
        <div
          class="bento-card {bentoCard.component} scale-up-on-hover"
          style="background-image: url('{bentoCard.image?.filename}');"
          on:click={() => openYoutubeModal(bentoCard.video[0]?.embedLink)}
          on:keydown={(e) => e.key === "Enter" && openYoutubeModal(bentoCard.video[0]?.embedLink)}
          role="button"
          tabindex="0"
        >
          <div class="bento-card-content">
            <div class="bento-card-icon-text-wrapper">
              <img
                class="bento-card-icon"
                src="/images/home/bento/bento-icon-video.svg"
                alt="Video icon"
              />
              <p class="bento-card-text">
                <span class="bento-card-caption">{bentoCard.caption}</span>
              </p>
            </div>
          </div>
        </div>
      {/if}
    {/each}
  </div>

  <div class="social-links">
    {#each socials as social}
      <a
        class="social-link"
        href={social.link?.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={social.name}
      >
        {#if social.icon}
          <img class="social-icon" src={social.icon.filename} alt={social.name} />
        {/if}
      </a>
    {/each}
  </div>
</section>

<style lang="postcss">
  .bento-box-section {
    margin-top: 3rem;

    @media (min-width: 768px) {
      margin-top: 5rem;
    }
  }

  .main-heading {
    text-align: center;
    margin-bottom: 4rem;
  }

  /* Bento grid */

  .bento-box {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-areas:
      "a a"
      "b c"
      "d e"
      "d f"
      "g g";
    gap: 1rem;

    @media (min-width: 1025px) {
      min-height: 460px;
      grid-template-columns: repeat(6, 1fr);
      grid-template-areas:
        "a a b c d e"
        "a a g g d f";
    }
  }

  .bento-card {
    overflow: hidden;
    background-size: cover;
    background-position-x: center;
    background-position-y: bottom;
    background-repeat: no-repeat;
    background-color: #ebe8e1;
    color: white;
    border-radius: 0.5rem;
  }

  .bento-card:nth-child(1) {
    grid-area: a;
    min-height: 240px;
  }

  .bento-card:nth-child(2) {
    grid-area: b;
    aspect-ratio: 1 / 1;
  }

  .bento-card:nth-child(3) {
    grid-area: c;
    aspect-ratio: 1 / 1;
  }

  .bento-card:nth-child(4) {
    grid-area: d;
  }

  .bento-card:nth-child(5) {
    grid-area: e;
    aspect-ratio: 1 / 1;
  }

  .bento-card:nth-child(6) {
    grid-area: f;
    aspect-ratio: 1 / 1;
  }

  .bento-card:nth-child(7) {
    grid-area: g;
    aspect-ratio: 2 / 1;
  }

  @media (min-width: 1024px) {
    .bento-card {
      aspect-ratio: auto !important;
    }
  }

  .bento-card-content {
    height: 100%;
  }

  /* Quote Card content */

  .bentoCardQuote .bento-card-content {
    display: grid;
    grid-template-rows: 1fr auto;
    gap: 2rem;
    padding: 1rem;
  }

  .bento-card-quotation {
    font-family: var(--font-secondary);
    font-weight: var(--font-weight-semibold);
    font-size: 26px;
    line-height: 1.1;
    color: #fff;

    @media (min-width: 768px) {
      font-size: 36px;
    }
  }

  .bento-card-author-location-wrapper {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  .bento-card-author {
    font-weight: bold;
    font-size: 14px;
  }

  .bento-card-location {
    font-size: 14px;
  }

  /* Link, Podcast, and Video Card content */

  .bentoCardLink,
  .bentoCardVideo {
    cursor: pointer;
  }

  .bentoCardLink .bento-card-content,
  .bentoCardImage .bento-card-content,
  .bentoCardVideo .bento-card-content {
    display: grid;
    grid-template-rows: 1fr;
    align-items: end;
    padding: 1rem;
  }

  .bento-card-icon-text-wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .bento-card-icon {
    height: 16px;
    width: 16px;
    margin-right: 0.5rem;
  }

  .bento-card-text {
    font-size: 11px;
    font-weight: bold;
    line-height: 1.1;
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.5);
    text-transform: uppercase;
  }

  /* Social links */

  .social-links {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1.5rem;
    margin-top: 2rem;
  }

  .social-icon {
    width: 22px;
    height: 22px;
  }
</style>
